<script setup>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import axios from 'axios';
import { updateBannerVisible } from '@/helpers/bannerState';

// Zeigt einen Banner, sobald ein neues Deployment erkannt wird: die im Bundle
// eingebrannte Build-ID weicht von der aktuell ausgelieferten /version.json ab.
const CURRENT_BUILD_ID = String(process.env.VUE_APP_BUILD_ID || '');
const CHECK_INTERVAL_MS = 5 * 60 * 1000;

const updateAvailable = ref(false);
watch(updateAvailable, (visible) => { updateBannerVisible.value = visible; });
const dismissedBuildId = ref('');
let timer = null;

const checkForUpdate = async () => {
  if (!CURRENT_BUILD_ID) return;
  try {
    const res = await axios.get(`/version.json?ts=${Date.now()}`, {
      headers: { 'Cache-Control': 'no-store' },
      withCredentials: false,
    });
    const remoteBuildId = String(res.data?.buildId || '');
    if (!remoteBuildId || remoteBuildId === CURRENT_BUILD_ID) return;
    if (remoteBuildId === dismissedBuildId.value) return;
    updateAvailable.value = true;
  } catch (_) {
    // Netzwerkfehler/Deploy-Lücke: still bleiben, nächster Check kommt.
  }
};

const reloadApp = () => {
  // Harter Reload derselben URL — man bleibt auf der aktuellen Seite.
  window.location.reload();
};

const dismiss = async () => {
  try {
    const res = await axios.get(`/version.json?ts=${Date.now()}`, { withCredentials: false });
    dismissedBuildId.value = String(res.data?.buildId || '');
  } catch (_) {
    dismissedBuildId.value = 'dismissed';
  }
  updateAvailable.value = false;
};

const onFocus = () => checkForUpdate();

onMounted(() => {
  timer = window.setInterval(checkForUpdate, CHECK_INTERVAL_MS);
  window.addEventListener('focus', onFocus);
  // Erster Check leicht verzögert (Deploy direkt beim Laden ist unwahrscheinlich).
  window.setTimeout(checkForUpdate, 30 * 1000);
});

onBeforeUnmount(() => {
  updateBannerVisible.value = false;
  if (timer) window.clearInterval(timer);
  window.removeEventListener('focus', onFocus);
});
</script>

<template>
  <div v-if="updateAvailable" class="app-update-banner shadow-sm" role="alert">
    <i class="ri-refresh-line me-2"></i>
    <span>Eine neue Version des vManagers ist verfügbar.</span>
    <button type="button" class="btn btn-sm btn-light fw-semibold ms-3" @click="reloadApp">
      Jetzt aktualisieren
    </button>
    <button type="button" class="btn-close btn-close-white ms-3" aria-label="Später" title="Später" @click="dismiss"></button>
  </div>
</template>

<style scoped>
.app-update-banner {
  position: fixed;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  z-index: 3000;
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  margin-top: 0.5rem;
  border-radius: 0.5rem;
  background: var(--vz-primary, #405189);
  color: #fff;
  font-size: 0.9rem;
}
</style>
